import { z } from "zod";

import {
	type AircraftVrefProfile, CJP_VREF_PROFILES,
} from "./foqa-vref";

/**
 * Per-model AFM performance lookups — Vref, landing distance, approach climb — as data.
 *
 * The AFM does not publish one representation, so this models two storage FORMS:
 *
 *   grid                    nested cell arrays in axis order; `null` = NOT PERMITTED (the
 *                           `-`/shaded cells). That is data, not missing data, and must
 *                           never be interpolated through.
 *   reference_coefficients  one reference value + signed per-unit deltas + conditional
 *                           rules (Operational Landing Distance, 5-10-15). It cannot be
 *                           flattened into a grid without inventing values.
 *
 * Units live on the axes and on the result because a unit mix-up here is a silent scoring
 * bug, not a crash.
 *
 * Versioning matches foqa_thresholds/foqa_flags (`version` + `active`) so a deploy cannot
 * silently change historical scoring. Unlike those, this is NOT fleet-scoped: an airframe's
 * performance is physics, not cohort policy.
 */

export const FoqaPerformanceKindSchema = z.enum([
	"vref",
	"landing_distance",
	"landing_distance_correction",
	"approach_climb",
]);

export type FoqaPerformanceKind = z.infer<typeof FoqaPerformanceKindSchema>;

export const FoqaPerformanceFormSchema = z.enum(["grid", "reference_coefficients"]);

export type FoqaPerformanceForm = z.infer<typeof FoqaPerformanceFormSchema>;

/**
 * Drop keys that do not constrain, so "does not vary with it" has exactly ONE encoding.
 *
 * `config` and `applicability` are part of a row's identity (the unique index), and jsonb
 * equality is structural: `{}` and `{"landingFlap":null}` are different keys to Postgres but
 * identical to resolution, so storing both would let two rival active rows coexist.
 *
 * An empty array is the same trap in another spelling — resolution tests tailNumbers with
 * `?.length`, so `[]` reads as unconstrained while remaining distinct jsonb.
 */
// Empty strings count as unconstrained: "" would mint a rival identity chain that the
// resolver still ranks as type-wide (configMatches/applicability treat it as no constraint).
const stripUnconstrained = <T extends object>(value: T): T =>
	Object.fromEntries(
		Object.entries(value).filter(([, v]) =>
			v != null
			&& !(Array.isArray(v) && v.length === 0)
			&& !(typeof v === "string" && v.trim() === "")),
	) as T;

/**
 * The configuration a table was published for. A key left null/absent means the table does
 * not vary with it, so it matches any flight (see configMatches in foqa-performance-resolve).
 * Flap positions stay text: the AFM labels them "1"/"2"/"3"/"FULL".
 */
export const FoqaPerformanceConfigSchema = z.object({
	landingFlap: z.string().nullable().optional(),
	approachFlap: z.string().nullable().optional(),
	iceProtection: z.enum(["off", "on"]).nullable().optional(),
	wingstab: z.enum(["off", "on"]).nullable().optional(),
	runway: z.enum(["dry", "wet", "contaminated"]).nullable().optional(),
	brakingAction: z.string().nullable().optional(),
}).transform(stripUnconstrained);

export type FoqaPerformanceConfig = z.infer<typeof FoqaPerformanceConfigSchema>;

export const FOQA_PERFORMANCE_CONFIG_KEYS = [
	"landingFlap",
	"approachFlap",
	"iceProtection",
	"wingstab",
	"runway",
	"brakingAction",
] as const satisfies readonly (keyof FoqaPerformanceConfig)[];

/**
 * A named axis with its unit. `values` are the published breakpoints a grid interpolates
 * between, strictly ascending so bracketing is unambiguous (extraction normalizes AFM
 * column order). A reference_coefficients table has no breakpoints and lists its axes only
 * to pin the units its coefficients are expressed in.
 */
export const FoqaPerformanceAxisSchema = z.object({
	name: z.string().min(1),
	unit: z.string().min(1),
	values: z.array(z.number()).min(1).optional(),
}).refine(
	(axis) => axis.values === undefined || axisBreakpointsUsable(axis.values),
	{ message: "axis values must be finite and strictly ascending", path: ["values"] },
);

export type FoqaPerformanceAxis = z.infer<typeof FoqaPerformanceAxisSchema>;

/**
 * Breakpoints a grid can bracket in unambiguously. Enforced on the write path by the schema
 * above AND on the read path by the resolver, because a row loaded from jsonb never passed
 * through zod and a mis-ordered axis silently answers with the wrong cell.
 */
export function axisBreakpointsUsable (values: number[] | undefined): boolean {
	return !!values?.length
		&& values.every((v, i) => Number.isFinite(v) && (i === 0 || v > values[i - 1]));
}

/** Nested one level per axis, in axis order; a leaf of `null` is "not permitted". */
export type FoqaPerformanceCells = (number | null) | FoqaPerformanceCells[];

export const FoqaPerformanceCellsSchema: z.ZodType<FoqaPerformanceCells> = z.lazy(() =>
	z.union([z.number().nullable(), z.array(FoqaPerformanceCellsSchema)]));

/**
 * The cells nest exactly one level per axis, each level as long as that axis' breakpoints.
 *
 * An extraction that produces one value too many or too few still parses as cells, so this
 * is what separates "a grid" from "this grid's axes" — without it a short row answers with a
 * neighbouring cell and an over-long row hides published values.
 */
export const gridCellsMatchAxes = (
	cells: FoqaPerformanceCells | null | undefined,
	axes: readonly FoqaPerformanceAxis[],
): cells is FoqaPerformanceCells => {
	const walk = (node: FoqaPerformanceCells, depth: number): boolean => {
		if (depth === axes.length) {
			return !Array.isArray(node);
		}
		const { values } = axes[depth];
		return Array.isArray(node)
			&& values !== undefined
			&& node.length === values.length
			&& node.every((child) => walk(child, depth + 1));
	};
	return cells != null && axes.length > 0 && walk(cells, 0);
};

/**
 * A per-unit correction off the reference value.
 *
 * `delta` is the change to the result per `per` units of SIGNED deviation from the
 * reference. `deltaBelow`/`deltaAbove` are the AFM's asymmetric printings — each is the
 * change per `per` units of deviation MAGNITUDE in that direction, so the sheet's
 * "−133 head / +552 tail" transcribes verbatim without flipping signs by hand.
 *
 * Publishing only one direction is legitimate (the AFM corrects one way and not the other);
 * publishing none is an extraction that lost its numbers, so it is rejected here.
 */
export const FoqaPerformanceCoefficientSchema = z.object({
	axis: z.string().min(1),
	per: z.number().positive(),
	delta: z.number().optional(),
	deltaBelow: z.number().optional(),
	deltaAbove: z.number().optional(),
}).refine(
	(c) => c.delta !== undefined || c.deltaBelow !== undefined || c.deltaAbove !== undefined,
	{ message: "coefficient must publish delta, deltaBelow or deltaAbove" },
);

export type FoqaPerformanceCoefficient = z.infer<typeof FoqaPerformanceCoefficientSchema>;

/** Conditional correction (AFM "overweight: +121 ft per 200 lb above 16500 lb"). */
export const FoqaPerformanceRuleSchema = z.object({
	when: z.object({
		axis: z.string().min(1),
		above: z.number().optional(),
		below: z.number().optional(),
	}),
	per: z.number().positive(),
	delta: z.number(),
});

export type FoqaPerformanceRule = z.infer<typeof FoqaPerformanceRuleSchema>;

export const FoqaPerformanceReferenceSchema = z.object({
	value: z.number(),
	/** Axis values the reference was taken at; an axis omitted here is referenced at 0. */
	at: z.record(z.string(), z.number()).optional(),
	coefficients: z.array(FoqaPerformanceCoefficientSchema),
	rules: z.array(FoqaPerformanceRuleSchema).optional(),
});

export type FoqaPerformanceReference = z.infer<typeof FoqaPerformanceReferenceSchema>;

/** The per-airframe hook: MSN ranges, engine variants and tails split cells within a type. */
export const FoqaPerformanceApplicabilitySchema = z.object({
	msnFrom: z.number().nullable().optional(),
	msnTo: z.number().nullable().optional(),
	engine: z.string().nullable().optional(),
	tailNumbers: z.array(z.string().trim().min(1)).nullable().optional(),
}).transform(stripUnconstrained);

export type FoqaPerformanceApplicability = z.infer<typeof FoqaPerformanceApplicabilitySchema>;

/** Provenance — what an extraction has to carry to be re-verifiable against the AFM page. */
export const FoqaPerformanceSourceSchema = z.object({
	document: z.string().min(1),
	revision: z.string().nullable().optional(),
	section: z.string().nullable().optional(),
	page: z.number().nullable().optional(),
	extractedAt: z.string().nullable().optional(),
	extractedBy: z.string().nullable().optional(),
	verifiedBy: z.string().nullable().optional(),
	verifiedAt: z.string().nullable().optional(),
	notes: z.string().nullable().optional(),
});

export type FoqaPerformanceSource = z.infer<typeof FoqaPerformanceSourceSchema>;

/**
 * The unit of the looked-up value, plus how the published table rounds. Rounding belongs to
 * the table (AFM Vref columns are whole knots), not to whoever calls the lookup.
 */
export const FoqaPerformanceResultSchema = z.object({
	unit: z.string().min(1),
	round: z.enum(["nearest", "none"]).optional(),
	/** "step_up" = the manual forbids interpolating between published values and requires the
	 *  next highest one (Phenom POH 5-10-10 wet/contaminated). Default "interpolate". */
	lookup: z.enum(["interpolate", "step_up"]).optional(),
});

export type FoqaPerformanceResult = z.infer<typeof FoqaPerformanceResultSchema>;

/** The fields resolution reads — satisfied by a foqa_performance_tables row and by fixtures. */
export interface FoqaPerformanceTableData {
	/** Present on stored rows; resolution uses it only as a deterministic last tie-break. */
	id?: string;
	aircraftModel: string;
	kind: FoqaPerformanceKind;
	form: FoqaPerformanceForm;
	config: FoqaPerformanceConfig;
	axes: FoqaPerformanceAxis[];
	cells: FoqaPerformanceCells | null;
	reference: FoqaPerformanceReference | null;
	result: FoqaPerformanceResult | null;
	applicability: FoqaPerformanceApplicability | null;
	version: number;
	active: boolean;
}

export interface FoqaPerformanceTableSeed extends FoqaPerformanceTableData {
	applicability: FoqaPerformanceApplicability;
	source: FoqaPerformanceSource;
}

/**
 * A whole writable row. Anything that writes a performance table parses through this first:
 * the payloads are jsonb, so this is the only place the forms' invariants (a grid whose
 * cells match its axes, a reference form that carries a reference) can be enforced before
 * the row becomes a number someone scores against.
 */
export const FoqaPerformanceTableRowSchema = z.object({
	aircraftModel: z.string().min(1),
	kind: FoqaPerformanceKindSchema,
	form: FoqaPerformanceFormSchema,
	config: FoqaPerformanceConfigSchema,
	axes: z.array(FoqaPerformanceAxisSchema),
	cells: FoqaPerformanceCellsSchema.nullable(),
	reference: FoqaPerformanceReferenceSchema.nullable(),
	result: FoqaPerformanceResultSchema.nullable(),
	applicability: FoqaPerformanceApplicabilitySchema,
	source: FoqaPerformanceSourceSchema,
	version: z.number().int().positive(),
	active: z.boolean(),
}).superRefine((row, ctx) => {
	if (row.form === "grid" && !gridCellsMatchAxes(row.cells, row.axes)) {
		ctx.addIssue({
			code: "custom",
			message: "grid cells must nest one level per axis, sized to its breakpoints",
			path: ["cells"],
		});
	}
	if (row.form === "reference_coefficients" && row.reference === null) {
		ctx.addIssue({
			code: "custom",
			message: "reference_coefficients table must carry a reference",
			path: ["reference"],
		});
	}
});

const CJP_VREF_SOURCE: FoqaPerformanceSource = {
	document: "CJP FOQA Users Guide",
	section: "appendix — Method to Approximate Gross Weight and Vref",
	extractedBy: "seed",
};

const PHENOM_VREF_SOURCE: FoqaPerformanceSource = {
	document: "Flysto ground-truth corpus fit",
	section: "scripts/fit-phenom-vref.ts",
	extractedBy: "seed",
	notes: "Fitted Vref = 99.6 + 5.49·(GW − 13,999)/1000 (RMSE 1.35 kt); ZFW anchored at MZFW.",
};

/**
 * A model's flat CJP weight→Vref list as a single-axis grid table.
 *
 * The seed list is descending by weight and axes are stored ascending, so both the axis and
 * the cells are reversed together — never one without the other.
 */
export const vrefPerformanceTable = (
	aircraftModel: string,
	profile: AircraftVrefProfile,
): FoqaPerformanceTableSeed => {
	const ascending = [...profile.table].sort((a, b) => a[0] - b[0]);
	return {
		aircraftModel,
		kind: "vref",
		form: "grid",
		config: {},
		axes: [{
			name: "weightLb", unit: "lb", values: ascending.map(([weightLb]) => weightLb), 
		}],
		cells: ascending.map(([, vrefKt]) => vrefKt),
		reference: null,
		result: { unit: "kt", round: "nearest" },
		applicability: {},
		source: aircraftModel === "phenom300" ? PHENOM_VREF_SOURCE : CJP_VREF_SOURCE,
		version: 1,
		active: true,
	};
};

/** The 9 seeded Vref profiles expressed as kind:'vref' performance tables. */
export const VREF_PERFORMANCE_TABLES: FoqaPerformanceTableSeed[] =
	Object.entries(CJP_VREF_PROFILES).map(([model, profile]) => vrefPerformanceTable(model, profile));
